<template>
  <v-dialog v-model="isVisible" max-width="900px" persistent>
    <v-card>
      <v-card-title class="popup-header">
        <span class="headline white-text">Solicitudes de Cambio de Fichaje</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="closePopup" class="white-icon">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="popup-content">
        <!-- Loading state -->
        <div v-if="loading" class="text-center my-6">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p class="mt-2">Cargando solicitudes...</p>
        </div>

        <!-- Empty state -->
        <div v-else-if="pendingModifications.length === 0" class="empty-state">
          <v-icon size="64" color="grey lighten-1">mdi-check-circle-outline</v-icon>
          <p class="subtitle-text mt-4">No hay solicitudes pendientes de aprobaci&oacute;n.</p>
        </div>

        <!-- List of pending modifications -->
        <div v-else>
          <v-list class="solicitudes-list">
            <v-list-item
              v-for="modification in pendingModifications"
              :key="modification.uuid"
              class="solicitud-item"
            >
              <v-list-item-content>
                <div class="solicitud-card">
                  <!-- Header with employee info -->
                  <div class="solicitud-header">
                    <v-avatar size="40" color="primary" class="mr-3">
                      <span class="white--text">{{ getInitials(modification.user_info.username) }}</span>
                    </v-avatar>
                    <div class="employee-info">
                      <span class="employee-name">{{ modification.user_info.username }}</span>
                      <span class="employee-email" v-if="modification.user_info.email">{{ modification.user_info.email }}</span>
                    </div>
                  </div>

                  <!-- Pending changes for this clock -->
                  <div
                    v-for="change in modification.pending_changes"
                    :key="change.index"
                    class="change-details"
                  >
                    <div class="change-row">
                      <div class="change-info">
                        <div class="change-type">
                          <v-chip small :color="getTypeColor(change.type)" dark>
                            {{ formatType(change.type) }}
                          </v-chip>
                        </div>
                        <div class="timestamps">
                          <div class="timestamp-item">
                            <span class="timestamp-label">Hora actual:</span>
                            <span class="timestamp-value current">{{ formatDateTime(modification.timestamp) }}</span>
                          </div>
                          <v-icon small class="mx-2">mdi-arrow-right</v-icon>
                          <div class="timestamp-item">
                            <span class="timestamp-label">Hora solicitada:</span>
                            <span class="timestamp-value requested">{{ formatDateTime(change.timestamp) }}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="justification" v-if="change.justification">
                      <v-icon small class="mr-1">mdi-comment-text-outline</v-icon>
                      <span>{{ change.justification }}</span>
                    </div>

                    <!-- Action buttons -->
                    <div class="action-buttons">
                      <v-btn
                        color="success"
                        small
                        @click="approveChange(modification.uuid, change.index)"
                        :loading="processingChange === `${modification.uuid}-${change.index}`"
                        :disabled="processingChange !== null"
                      >
                        <v-icon left small>mdi-check</v-icon>
                        {{ change.index === 0 ? 'Crear fichaje' : 'Aprobar' }}
                      </v-btn>
                      <v-btn
                        color="error"
                        small
                        outlined
                        @click="rejectChange(modification.uuid, change.index)"
                        :loading="processingChange === `${modification.uuid}-${change.index}-reject`"
                        :disabled="processingChange !== null"
                      >
                        <v-icon left small>mdi-close</v-icon>
                        Rechazar
                      </v-btn>
                    </div>
                  </div>
                </div>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>
      </v-card-text>

      <v-card-actions class="popup-actions">
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          text
          @click="loadPendingModifications"
          :loading="loading"
        >
          <v-icon left small>mdi-refresh</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import { query_dispatcher, sync_command_dispatcher } from "@/api/client";

export default {
  name: 'SolicitudesAdminPopup',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    companydbId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      isVisible: false,
      loading: false,
      pendingModifications: [],
      processingChange: null,
      snackbar: {
        show: false,
        text: '',
        color: 'success'
      }
    };
  },
  computed: {
    ...mapGetters({
      userData: 'me/userData'
    })
  },
  watch: {
    visible(newValue) {
      this.isVisible = newValue;
      if (newValue) {
        this.loadPendingModifications();
      }
    },
    isVisible(newValue) {
      if (!newValue) {
        this.$emit('close');
      }
    }
  },
  methods: {
    closePopup() {
      this.isVisible = false;
    },

    async loadPendingModifications() {
      this.loading = true;
      try {
        const result = await query_dispatcher.dispatch('get_pending_clock_modifications.query', {});
        this.pendingModifications = result || [];
      } catch (error) {
        console.error('Error loading pending modifications:', error);
        this.showSnackbar('error', 'Error al cargar las solicitudes');
      } finally {
        this.loading = false;
      }
    },

    async approveChange(clockUuid, changeIndex) {
      this.processingChange = `${clockUuid}-${changeIndex}`;
      try {
        await sync_command_dispatcher.dispatch('approve_clock_modification', {
          clock_uuid: clockUuid,
          change_index: changeIndex,
          admin_user_id: this.userData.id,
          new_status: 'validated'
        });

        this.showSnackbar('success', 'Solicitud aprobada correctamente');
        await this.loadPendingModifications();
      } catch (error) {
        console.error('Error approving change:', error);
        this.showSnackbar('error', 'Error al aprobar la solicitud');
      } finally {
        this.processingChange = null;
      }
    },

    async rejectChange(clockUuid, changeIndex) {
      this.processingChange = `${clockUuid}-${changeIndex}-reject`;
      try {
        await sync_command_dispatcher.dispatch('approve_clock_modification', {
          clock_uuid: clockUuid,
          change_index: changeIndex,
          admin_user_id: this.userData.id,
          new_status: 'rejected'
        });

        this.showSnackbar('success', 'Solicitud rechazada');
        await this.loadPendingModifications();
      } catch (error) {
        console.error('Error rejecting change:', error);
        this.showSnackbar('error', 'Error al rechazar la solicitud');
      } finally {
        this.processingChange = null;
      }
    },

    showSnackbar(color, text) {
      this.snackbar.color = color;
      this.snackbar.text = text;
      this.snackbar.show = true;
    },

    getInitials(username) {
      if (!username) return '?';
      return username.substring(0, 2).toUpperCase();
    },

    formatType(type) {
      const types = {
        'in': 'Entrada',
        'out': 'Salida',
        'pause': 'Pausa',
        'continue': 'Continuar'
      };
      return types[type] || type;
    },

    getTypeColor(type) {
      const colors = {
        'in': 'green',
        'out': 'red',
        'pause': 'orange',
        'continue': 'blue'
      };
      return colors[type] || 'grey';
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.popup-header {
  background-color: var(--Primary-700, #7339B7);
  color: white;
  padding: 16px 24px;
}

.popup-header .headline {
  color: white !important;
}

.white-icon {
  color: white !important;
}

.popup-content {
  padding: 24px;
  min-height: 300px;
  max-height: 600px;
  overflow-y: auto;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
}

.subtitle-text {
  color: #666;
  font-size: 14px;
}

.solicitudes-list {
  padding: 0;
}

.solicitud-item {
  margin-bottom: 16px;
  padding: 0;
}

.solicitud-card {
  width: 100%;
  background: #f9f9f9;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e0e0e0;
}

.solicitud-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
}

.employee-info {
  display: flex;
  flex-direction: column;
}

.employee-name {
  font-weight: 600;
  font-size: 16px;
  color: #333;
}

.employee-email {
  font-size: 12px;
  color: #666;
}

.change-details {
  background: white;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 8px;
  border: 1px solid #eee;
}

.change-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.change-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.timestamps {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.timestamp-item {
  display: flex;
  flex-direction: column;
}

.timestamp-label {
  font-size: 10px;
  color: #999;
  text-transform: uppercase;
}

.timestamp-value {
  font-size: 14px;
  font-weight: 500;
}

.timestamp-value.current {
  color: #666;
}

.timestamp-value.requested {
  color: #7339B7;
}

.justification {
  display: flex;
  align-items: flex-start;
  padding: 8px;
  background: #f5f5f5;
  border-radius: 4px;
  margin: 8px 0;
  font-size: 13px;
  color: #555;
}

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 12px;
}

.popup-actions {
  padding: 16px 24px;
  background-color: #f5f5f5;
}
</style>
